import React from "react";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { api } from "./axiosClient"; // ✅ use api client (baseURL = Render)
import {
  Mail,
  MessageCircle,
  MapPin,
  Instagram,
  Linkedin,
  ArrowRight,
} from "lucide-react";

const ContactPage = () => {
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    const payload = {
      name: formData.get("name"),
      email: formData.get("email"),
      company: formData.get("company") || "",
      country: formData.get("country") || "",
      reason: formData.get("reason") || "General question",
      message: formData.get("message"),
      isBulk: formData.get("isBulk") === "on",
    };

    try {
      await api.post("/api/contact", payload); // ✅ goes to Render
      toast.success("Message sent! Team NutraFox will get back to you soon.");
      e.target.reset();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || "Failed to send message. Please try again."
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF5] pt-12 pb-4 px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Top badge */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="inline-flex items-center gap-2 rounded-full bg-white/80 border border-emerald-100 px-3 py-1 text-xs sm:text-sm text-emerald-800 mb-3"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span>Sourcing Nature. Shipping Purity.</span>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
          {/* LEFT: Info + cards */}
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
          >
            <h1 className="text-3xl sm:text-4xl lg:text-[2.6rem] font-semibold text-[#1E5631] tracking-tight mb-2">
              Let’s talk{" "}
              <span className="bg-gradient-to-r from-emerald-600 to-lime-500 bg-clip-text text-transparent">
                Makhana
              </span>
              .
            </h1>

            <p className="text-sm sm:text-base text-slate-600 max-w-xl mb-4">
              Whether you&apos;re a distributor, retailer, or a curious foxnut
              lover—Team NutraFox is just a message away. Share your ideas,
              orders, or partnership plans and we&apos;ll get back within{" "}
              <span className="font-semibold text-emerald-700">24–48 hours</span>.
            </p>

            {/* Quick info cards */}
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {/* Email */}
              <div className="rounded-2xl bg-white shadow-sm border border-emerald-50 p-4 flex items-start gap-3">
                <div className="rounded-xl bg-emerald-50 p-2">
                  <Mail className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-medium tracking-wide">
                    Email
                  </p>
                  <p className="text-sm font-semibold text-slate-800">
                    thenutrafox@gmail.com
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    For orders, collaborations &amp; export queries.
                  </p>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="rounded-2xl bg-white shadow-sm border border-emerald-50 p-4 flex items-start gap-3">
                <div className="rounded-xl bg-emerald-50 p-2">
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-medium tracking-wide">
                    WhatsApp
                  </p>
                  <p className="text-sm font-semibold text-slate-800">
                    +91-9631035342
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Quick questions, order follow-ups &amp; samples.
                  </p>
                </div>
              </div>

              {/* Origin */}
              <div className="rounded-2xl bg-white shadow-sm border border-emerald-50 p-4 flex items-start gap-3">
                <div className="rounded-xl bg-emerald-50 p-2">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-medium tracking-wide">
                    Origin &amp; Operations
                  </p>
                  <p className="text-sm font-semibold text-slate-800">
                    Bihar • India
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Sourcing directly from the heart of the makhana belt.
                  </p>
                </div>
              </div>

              {/* Social */}
              <div className="rounded-2xl bg-white shadow-sm border border-emerald-50 p-4 flex items-start gap-3">
                <div className="rounded-xl bg-emerald-50 p-2">
                  <Instagram className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-[11px] text-slate-500 uppercase font-medium tracking-wide">
                    Social
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <a
                      href="https://www.instagram.com/thenutrafox/"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                    >
                      Instagram
                      <ArrowRight className="w-3 h-3" />
                    </a>
                    <span className="h-1 w-1 rounded-full bg-slate-300" />
                    <a
                      href="https://www.linkedin.com/company/thenutrafox/"
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800"
                    >
                      LinkedIn
                      <Linkedin className="w-3 h-3" />
                    </a>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Follow our sourcing stories &amp; product drops.
                  </p>
                </div>
              </div>
            </div>

            {/* Export CTA */}
            <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/80 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="text-xs sm:text-[13px] text-emerald-900">
                <span className="font-semibold">Looking for bulk or export?</span>{" "}
                We have a dedicated form for international &amp; large volume
                orders.
              </div>
              <a
                href="/export-enquiry"
                className="inline-flex items-center justify-center rounded-full bg-[#1E5631] text-white text-xs sm:text-sm font-medium px-4 py-1.5 hover:bg-[#174326] transition"
              >
                Go to Export Enquiry
                <ArrowRight className="w-3 h-3 ml-1" />
              </a>
            </div>
          </motion.div>

          {/* RIGHT: Contact form */}
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
            className="mt-[-28px]" // pulls form closer to navbar
          >
            <div className="relative">
              <div className="absolute -inset-2 rounded-3xl bg-gradient-to-br from-emerald-200/30 via-lime-200/30 to-amber-100/30 blur-xl -z-10" />
              <div className="rounded-3xl bg-white shadow-xl border border-emerald-50/80 p-5 sm:p-6 lg:p-7">
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-1">
                  Send us a message
                </h2>
                <p className="text-xs sm:text-sm text-slate-500 mb-4">
                  Tell us a bit about what you&apos;re looking for—orders,
                  partnerships, white-labelling, or just saying hello.
                </p>

                <form className="space-y-3" onSubmit={handleSubmit}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Full Name
                      </label>
                      <input
                        type="text"
                        required
                        name="name"
                        placeholder="eg. Priyanka Sharma"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        name="email"
                        placeholder="you@example.com"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Company / Brand (optional)
                      </label>
                      <input
                        type="text"
                        name="company"
                        placeholder="eg. Fox Foods Pvt Ltd"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-medium text-slate-600 mb-1">
                        Country
                      </label>
                      <input
                        type="text"
                        name="country"
                        placeholder="eg. India, UAE, USA..."
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      I&apos;m reaching out about
                    </label>
                    <select
                      name="reason"
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition"
                    >
                      <option>General question</option>
                      <option>Retail / distribution inquiry</option>
                      <option>Bulk / export order</option>
                      <option>White-label / private label</option>
                      <option>Collaboration / partnership</option>
                      <option>Something else</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-[11px] font-medium text-slate-600 mb-1">
                      Tell us more
                    </label>
                    <textarea
                      name="message"
                      rows={2}
                      required
                      placeholder="Share quantity, timelines, packaging needs or any specific questions you have..."
                      className="w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-1.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition resize-none"
                    />
                  </div>

                  <div className="flex items-start gap-2">
                    <input
                      id="bulk"
                      name="isBulk"
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <label
                      htmlFor="bulk"
                      className="text-[11px] sm:text-xs text-slate-600 leading-snug"
                    >
                      I&apos;d like to discuss{" "}
                      <span className="font-semibold">
                        bulk / export requirements
                      </span>{" "}
                      (100kg+ per month).
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center rounded-2xl bg-[#1E5631] text-white text-sm font-semibold py-2.5 mt-1 hover:bg-[#174326] active:scale-[0.99] transition shadow-md shadow-emerald-900/10"
                  >
                    Send Message
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </button>

                  <p className="text-[11px] text-slate-500 text-center mt-1">
                    By submitting, you agree to be contacted by Team NutraFox for
                    this enquiry. We never spam.
                  </p>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
